import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, Renderer2, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { loadPaymentCardImage } from '@models/functions.model';
import {
  AdditionalInfoField,
  CustomElementValue,
  ExistingAssetDetails,
  OrderDetailsAdditionalInfo,
  OrderPriceSummary,
  OrderPriceSummaryItem,
  OrderTracker,
  SourceOrderDetails
} from '@models/shopping-cart.model';
import {
  ACTIVITY_3_ASSET_CARD_FIELDS,
  CART_ITEM_TYPE_ACCESSORY,
  CART_ITEM_TYPE_DEVICE
} from '@models/shopping.model';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { CustomizeService } from '@services/customize-service/customize.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { CopyComponent } from '@shared-components/copy/copy.component';
import { OrderTrackerComponent } from '@shared-components/order-tracker/order-tracker.component';
import { Subscription } from 'rxjs';
import { DomSanitizerPipe } from 'tangoe-design-system';

import { BulkOrderDetailsComponent } from '../bulk-order-details/bulk-order-details.component';
import { SelectedAssetComponent } from '../selected-asset/selected-asset.component';

@Component({
  selector: 'ts-order-details',
  templateUrl: './order-details.component.html',
  imports: [
    CommonModule,
    TranslationPipe,
    DomSanitizerPipe,
    CopyComponent,
    OrderTrackerComponent,
    SelectedAssetComponent,
    BulkOrderDetailsComponent,
    NgbTooltip
  ]
})
export class OrderDetailsComponent implements OnDestroy {
  @Input() order_id: number;
  @Input() order_tracker = [] as OrderTracker[];
  @Input() related_order_info: Array<{ id: number; url: string }> | undefined;
  @Input() related_order_url: string | undefined;
  @Input() order_status_desc: string;
  @Input() price_summary = {} as OrderPriceSummary | undefined;
  @Input() source_order_details = {} as SourceOrderDetails;
  @Input() asset_details: ExistingAssetDetails;
  @Input() order_type_id: number;
  @Input() additional_info: OrderDetailsAdditionalInfo;
  @Input() is_review: boolean = false;
  @Input() is_tmo_environment: boolean = false;
  @Input() is_summary_page: boolean = false;
  @Input() carrier_order_number: string | undefined;
  device_to_activate_fields: string[] = ACTIVITY_3_ASSET_CARD_FIELDS;
  device_type: string = CART_ITEM_TYPE_DEVICE;
  accessory_type: string = CART_ITEM_TYPE_ACCESSORY;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private customize_svc: CustomizeService,
    private renderer: Renderer2,
    private sanitizer: DomSanitizer,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get has_price_summary(): boolean {
    return !!this.price_summary && Object.keys(this.price_summary).length > 0;
  }

  get show_order_details(): boolean {
    return Boolean(
      this.source_order_details.shipping_address ||
      this.source_order_details.preferred_address ||
      this.source_order_details.shipping_method
    );
  }

  get shipping_address_label(): string {
    return [4, 13].includes(this.order_type_id)
      ? this.translation_svc.translate('return_shipping_address')
      : this.translation_svc.translate('shipping_address');
  }

  getFeatureType = (item: OrderPriceSummaryItem): string => this.customize_svc.featureType(item);

  isEmployeePayable(type?: string): boolean | undefined {
    if (type) {
      if (type === 'addon')
        return Boolean(
          this.price_summary?.one_time_costs?.find(
            (cost: OrderPriceSummaryItem) =>
              cost.employee_cost_to_currency && cost.type === 'ACCESSORY'
          )
        );
      else if (type === 'device')
        return Boolean(
          this.price_summary?.one_time_costs?.find(
            (cost: OrderPriceSummaryItem) =>
              cost.employee_cost_to_currency && cost.type === 'DEVICE'
          )
        );
    }

    return Boolean(
      this.price_summary?.one_time_costs?.find(
        (cost: OrderPriceSummaryItem) =>
          cost.employee_cost_to_currency && ['ACCESSORY', 'DEVICE'].includes(cost.type)
      )
    );
  }

  sortAdditionalInfo(fields: AdditionalInfoField[]): AdditionalInfoField[] {
    const index: number = fields.findIndex(
      (field: AdditionalInfoField) => field.type === 'textarea'
    );
    fields.push(fields.splice(index, 1)[0]);
    return fields;
  }

  getPaymentCardImage(card_type: string): string {
    return loadPaymentCardImage(card_type);
  }

  download(file_info: Partial<CustomElementValue>): void {
    this.subscriptions.add(
      this.shopping_svc.downloadFile(this.order_id, file_info).subscribe({
        next: (res: HttpResponse<Blob>) => {
          const blob: string = this.sanitizer.sanitize(
            SecurityContext.RESOURCE_URL,
            this.sanitizer.bypassSecurityTrustResourceUrl(
              URL.createObjectURL(new Blob([res.body!], { type: res.body!.type }))
            )
          )!;
          const link: HTMLAnchorElement = this.renderer.createElement('a');
          link.setAttribute('href', blob);
          link.setAttribute('download', file_info.file_name!);
          link.click();
          link.remove();
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }
}
